import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { forwardRef, MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import IconCartQuantity from "@/icons/icon-cart-quantity";
import IconMinus from "@/icons/icon-minus";
import IconPlus from "@/icons/icon-plus";
import InputField from "~/components/form/input-field";
import { useBreakpoint } from "~/contexts/breakpoint";
import { useObjectConfigurationValue } from "~/contexts/configuration";
import { useCartProduct } from "~/hooks/use-cart";
import Logger from "~/services/logger/logger";
import {
  QuantitySelectorDesktopFields,
  quantitySelectorDesktopValidationSchema,
  QuantitySelectorMobileFields,
  quantitySelectorMobileValidationSchema,
} from "~/shared/product/quantity-selector-fields";
import Algolia from "~/types/algolia";
import { EcommerceTrackingItemModel } from "~/types/trackings.models";
import Constants from "~/utils/constants";

import Button from "./button";
import Dialog from "./dialog";
import Icon from "./icon";
import styles from "./quantity-selector.module.scss";
import dialogStyles from "./quantity-selector-dialog.module.scss";

type PropsQuantitySelector = {
  productID: string;
  lineID?: string;
  maxQuantity?: number;
  customClassNames?: string;
  disableRemove?: boolean;
  disableAdd?: boolean;
  disableChangeQuantity?: boolean;
  onChange?: (qty: number, prev: number) => void;
  actionContext?: string;
  trackingData?: EcommerceTrackingItemModel;
  algolia?: Algolia.ProductTileInfo;
};

export default forwardRef<HTMLDivElement, PropsQuantitySelector>(function QuantitySelector(
  {
    productID,
    lineID,
    maxQuantity,
    customClassNames,
    disableRemove = false,
    disableAdd = false,
    disableChangeQuantity = false,
    actionContext = "",
    trackingData,
    algolia,
  }: PropsQuantitySelector,
  ref
) {
  const trackingItem = trackingData
    ? {
        context: actionContext,
        info: trackingData,
      }
    : undefined;

  const delayQuantityTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const { quantityInCart, changeQuantity } = useCartProduct(productID!, lineID || undefined, trackingItem);
  const t = useTranslations();
  const breakpoint = useBreakpoint();
  const configurationCartSettings = useObjectConfigurationValue("App:Ecommerce:CartSettings");
  const [newNumericalMaxQuantity, setNewNumericalMaxQuantity] = useState<number>();
  const [isDialogOpened, setIsDialogOpened] = useState<boolean>(false);
  const openModalButtonRef = useRef<HTMLButtonElement | null>(null);

  const numericalMaxQuantity = useMemo(() => {
    if (newNumericalMaxQuantity !== undefined) {
      return newNumericalMaxQuantity;
    }

    const rawEvaOrderLineMaximumQuantityOrdered: number | void =
      // Prevent null to be used because it wouldn't be NaN if converted
      configurationCartSettings?.OrderLineMaximumQuantityOrdered || void 0;
    const evaOrderLineMaximumQuantityOrdered: number =
      isNaN(+rawEvaOrderLineMaximumQuantityOrdered) || +rawEvaOrderLineMaximumQuantityOrdered < 1
        ? Infinity
        : +rawEvaOrderLineMaximumQuantityOrdered;

    return isNaN(+maxQuantity!)
      ? evaOrderLineMaximumQuantityOrdered
      : Math.min(evaOrderLineMaximumQuantityOrdered, +maxQuantity!);
  }, [configurationCartSettings, maxQuantity, newNumericalMaxQuantity]);

  // initialize product quantity form
  const { register, handleSubmit, setValue, getValues, clearErrors, formState } = useForm<
    ReturnType<typeof useBreakpoint> extends "desktop" ? QuantitySelectorDesktopFields : QuantitySelectorMobileFields
  >({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    shouldFocusError: breakpoint === "desktop" ? true : false,
    resolver:
      breakpoint === "desktop"
        ? zodResolver(quantitySelectorDesktopValidationSchema(numericalMaxQuantity))
        : zodResolver(quantitySelectorMobileValidationSchema(numericalMaxQuantity)),
  });
  const {
    ref: inputRef,
    onChange: inputOnChange,
    onBlur: inputOnBlur,
    ...inputRegisterRest
  } = register("Quantity", { valueAsNumber: true });
  const { errors } = formState;

  // things to do when async request of quantity change goes ok
  useEffect(() => {
    if (changeQuantity.isSuccess) {
      if (breakpoint === "mobile") {
        setIsDialogOpened(false);
      }
      changeQuantity.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeQuantity.isSuccess, breakpoint]);

  // in case of quantity mutation errors, print errors on console
  useEffect(() => {
    if (changeQuantity.error) {
      Logger.instance.error("Quantity selector errored > Change quantity", changeQuantity.error);
      changeQuantity.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeQuantity.error]);

  // In case of validation errors, cancel the debounce timer
  useEffect(() => {
    if (errors.Quantity) {
      if (delayQuantityTimeoutId.current) {
        clearTimeout(delayQuantityTimeoutId.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors.Quantity]);

  useEffect(() => {
    if (changeQuantity.data?.isOOS) {
      setValue("Quantity", 1, { shouldValidate: true, shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeQuantity.data?.isOOS, setValue]);

  useEffect(() => {
    const newMaxQuantity = changeQuantity.data?.newMaxQuantity;
    if (newMaxQuantity) {
      setValue("Quantity", newMaxQuantity, { shouldValidate: true, shouldDirty: true });
      setNewNumericalMaxQuantity(newMaxQuantity);
    }
  }, [changeQuantity.data?.newMaxQuantity, setValue]);

  // Clear the debounce timer when component unmounts
  useEffect(() => {
    setValue("Quantity", quantityInCart ?? 0, { shouldValidate: true, shouldDirty: true });
    return () => {
      if (delayQuantityTimeoutId.current) {
        clearTimeout(delayQuantityTimeoutId.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update the value of quantity a soon as quantityInCart changes
  useEffect(() => {
    setValue("Quantity", quantityInCart ?? 0, { shouldValidate: true, shouldDirty: true });
  }, [quantityInCart, setValue]);

  const handleClickIncrease = (evt?: MouseEvent): void => {
    const nextQuantity = Math.floor(getValues("Quantity")) + 1;
    setValue("Quantity", Math.max(0, Math.min(nextQuantity, numericalMaxQuantity)), {
      shouldValidate: true,
      shouldDirty: true,
    });
    if (!isDialogOpened) {
      handleSubmit(onSubmit)();
    }
  };

  const handleClickDecrease = (evt?: MouseEvent): void => {
    const nextQuantity = Math.ceil(getValues("Quantity")) - 1;
    setValue("Quantity", Math.max(0, Math.min(nextQuantity, numericalMaxQuantity)), {
      shouldValidate: true,
      shouldDirty: true,
    });
    if (!isDialogOpened) {
      handleSubmit(onSubmit)();
    }
  };

  const onSubmit: SubmitHandler<
    ReturnType<typeof useBreakpoint> extends "desktop" ? QuantitySelectorDesktopFields : QuantitySelectorMobileFields
  > = (
    data: ReturnType<typeof useBreakpoint> extends "desktop"
      ? QuantitySelectorDesktopFields
      : QuantitySelectorMobileFields
  ) => {
    // avoid submitting the form if the quantity submitted is the same as the quantity already in cart
    if (data.Quantity === quantityInCart) {
      return;
    }
    // constrain the value between min and max (unfortunately we cannot use schema trnasofmrations)
    if (data.Quantity > numericalMaxQuantity) {
      setValue("Quantity", numericalMaxQuantity, { shouldValidate: true, shouldDirty: true });
    }
    // clear debounce timeout if not already elapsed...
    if (delayQuantityTimeoutId.current) {
      clearTimeout(delayQuantityTimeoutId.current);
    }
    // ...and override it with another debounce timeout starting right away
    delayQuantityTimeoutId.current = setTimeout(() => {
      //handle algolia events
      if (data.Quantity > quantityInCart) {
        algolia?.events?.clickEvent?.(algolia.product);
      }

      changeQuantity.mutate({ ...data, maxQuantity: numericalMaxQuantity });
    }, Constants.DEFAULT_DEBOUNCE_TIME);

    if (isDialogOpened && getValues("Quantity") !== 0) {
      openModalButtonRef.current?.focus();
    }
  };

  // Dialog callbacks
  const onDialogClose = () => {
    if (delayQuantityTimeoutId.current) {
      clearTimeout(delayQuantityTimeoutId.current);
    }
    // when closing the dialog, align the form input to the value in cart
    if (!changeQuantity.isLoading) {
      setValue("Quantity", quantityInCart ?? 0, { shouldValidate: true, shouldDirty: true });
    }
    // Close the dialog
    setIsDialogOpened(false);
    if (getValues("Quantity") !== 0) {
      openModalButtonRef.current?.focus();
    }
  };

  return (
    <>
      <motion.div
        className={classNames(styles.quantitySelector, customClassNames)}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        tabIndex={-1}
        ref={ref}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={classNames(styles.quantityForm, changeQuantity.isLoading && styles.disabledQuantityForm)}
          noValidate
        >
          <button
            className={classNames("beacon-cart-mod-btn", styles.quantityButton)}
            type="button"
            disabled={disableRemove || changeQuantity.isLoading}
            onClick={(evt): void => {
              if (disableRemove !== true) {
                handleClickDecrease();
              }
            }}
            aria-label={t("a11y.quantityDecrease")}
          >
            <Icon icon={IconMinus} width={16} height={16} />
          </button>
          {breakpoint === "mobile" && (
            <button
              {...inputRegisterRest}
              ref={(element) => {
                inputRef(element);
                openModalButtonRef.current = element;
              }}
              aria-label={`${t("a11y.quantity")} ${getValues("Quantity")}`}
              className={styles.quantityInput}
              onClick={(): void => {
                setIsDialogOpened(true);
              }}
              onBlur={(evt): void => {
                inputOnBlur(evt);
              }}
            >
              {getValues("Quantity")}
            </button>
          )}
          {breakpoint === "desktop" && (
            <input
              {...inputRegisterRest}
              ref={inputRef}
              aria-label={t("a11y.quantity")}
              type="number"
              className={styles.quantityInput}
              defaultValue={quantityInCart ?? 0}
              readOnly={!disableChangeQuantity && breakpoint === "desktop" && changeQuantity.isLoading ? true : false}
              onBlur={(evt): void => {
                inputOnBlur(evt);

                if (isNaN(getValues("Quantity"))) {
                  setValue("Quantity", quantityInCart, { shouldValidate: true, shouldDirty: true });
                }
                if (getValues("Quantity") !== quantityInCart) {
                  handleSubmit(onSubmit)();
                }
              }}
            />
          )}
          <button
            className={classNames("beacon-cart-mod-btn", styles.quantityButton)}
            type="button"
            disabled={disableAdd || changeQuantity.isLoading}
            onClick={(evt): void => {
              if (disableAdd !== true) {
                handleClickIncrease();
              }
            }}
            aria-label={t("a11y.quantityIncrease")}
          >
            <Icon icon={IconPlus} width={16} height={16} />
          </button>
          <button
            type="submit"
            disabled={changeQuantity.isLoading}
            className={styles.submitButton}
            aria-label={t("generic.submit")}
          >
            {t("generic.submit")}
          </button>
        </form>
      </motion.div>
      {/* For a11y reasons, focusing on the field on mobile devices will open a dialog with the same form. Agreed with Kiko (Diego) and the design team on 2023/11/13.
          Update 2024/08/01: on mobile, when the modal closed, the focus returned to the starting point.
          To avoid this, the input was changed to a button and when the modal closed, the focus returned to the button */}
      {breakpoint === "mobile" ? (
        <Dialog
          isOpen={isDialogOpened}
          title={
            <>
              {t.rich("generic.productQuantityModifyTitle", {
                CartQuantityIcon: (chunks) => <Icon icon={IconCartQuantity} />,
              })}
            </>
          }
          onClose={onDialogClose}
          shouldReturnFocusAfterClose={false}
          customStyles={dialogStyles}
        >
          <p className={dialogStyles.description}>
            {t("generic.productQuantityModifyDescription", {
              maxProducts: numericalMaxQuantity,
            })}
          </p>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className={classNames(
              dialogStyles.quantityForm,
              changeQuantity.isLoading && dialogStyles.disabledQuantityForm
            )}
          >
            <div className={dialogStyles.quantityFormInputs}>
              <Button
                className={classNames("beacon-cart-mod-btn", dialogStyles.quantityButton)}
                type="button"
                disabled={disableRemove || changeQuantity.isLoading}
                onClick={(evt): void => {
                  if (disableRemove !== true) {
                    handleClickDecrease();
                  }
                }}
                aria-label={t("a11y.quantityDecrease")}
              >
                <Icon icon={IconMinus} width={16} height={16} />
              </Button>
              <InputField
                {...inputRegisterRest}
                ref={inputRef}
                label={t("a11y.quantity")}
                className={dialogStyles.quantityInput}
                customStyles={{
                  wrapperInputField: dialogStyles.wrapperInputField,
                  inputField: dialogStyles.inputField,
                }}
                aria-label={t("a11y.quantity")}
                type="number"
                defaultValue={quantityInCart ?? 0}
                readOnly={disableChangeQuantity || changeQuantity.isLoading}
                error={
                  errors.Quantity?.message
                    ? t(errors.Quantity?.message, {
                        fieldName: t("a11y.quantity"),
                        minValue: 0,
                        maxValue: numericalMaxQuantity,
                      })
                    : ""
                }
                onClearErrors={() => clearErrors("Quantity")}
                onBlur={(evt): void => {
                  inputOnBlur(evt);
                }}
              />
              <Button
                className={classNames("beacon-cart-mod-btn", dialogStyles.quantityButton)}
                type="button"
                disabled={disableAdd || changeQuantity.isLoading}
                onClick={(evt): void => {
                  if (disableAdd !== true) {
                    handleClickIncrease();
                  }
                }}
                aria-label={t("a11y.quantityIncrease")}
              >
                <Icon icon={IconPlus} width={16} height={16} />
              </Button>
            </div>
            <div className={dialogStyles.quantityFormSubmit}>
              <Button
                variant="secondary"
                type="reset"
                disabled={changeQuantity.isLoading}
                className={dialogStyles.resetButton}
                onClick={(evt): void => {
                  setValue("Quantity", quantityInCart ?? 0, { shouldValidate: true, shouldDirty: true });
                }}
              >
                {t("generic.cancel")}
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={changeQuantity.isLoading}
                loading={changeQuantity.isLoading}
                className={dialogStyles.submitButton}
              >
                {t("generic.confirmation")}
              </Button>
            </div>
          </form>
        </Dialog>
      ) : null}
    </>
  );
});
